import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatDisplayDate } from "../../../../common/utils/date";
import RecordTable, { RecordColumn } from "../common/RecordTable";
import PrescriptionStatusBadge from "./PrescriptionStatusBadge";
import PrescriptionTypeBadge from "./PrescriptionTypeBadge";
import {
  HistoryPrescriptionListItem,
  historyClientName,
} from "../../entities/history";

interface PrescriptionTableProps {
  rows: HistoryPrescriptionListItem[];
  loading?: boolean;
  /** Cross-client view: name the client on each row and offer a client filter. */
  showClient?: boolean;
  /** Banners (errors) rendered above the rows. */
  banners?: React.ReactNode;
  /**
   * Where the "Clear Filters" pill goes — History hands down a slot on its tab
   * line so the table doesn't shift when a filter is applied.
   */
  clearFiltersSlot?: HTMLElement | null;
  /** Remembers this listing's filters across navigation — see `RecordTable`. */
  filterPersistKey?: string;
}

/**
 * Status label, matching `PrescriptionStatusBadge` and the detail page:
 * expired first, whatever `active` says; otherwise Active or Inactive.
 */
const statusOf = (row: HistoryPrescriptionListItem): string =>
  row.expired ? "Expired" : row.active === 1 ? "Active" : "Inactive";

/** `sourceType`: 1 auto-generated, 2 auto-generated then edited, 3 manual. */
const TYPE_LABELS: Record<number, string> = {
  1: "Auto-generated",
  2: "Auto-generated - Edited",
  3: "Manual",
};

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** "2 services, 1 lab panel" — the listing carries counts, not names. */
const itemsOf = (row: HistoryPrescriptionListItem): string => {
  const parts: string[] = [];
  if (row.servicesCount > 0) parts.push(plural(row.servicesCount, "service"));
  if (row.labsCount > 0) parts.push(plural(row.labsCount, "lab panel"));
  return parts.length ? parts.join(", ") : "—";
};

/**
 * The prescription listing.
 *
 * The shared `RecordTable`, so it carries the same row card and in-header
 * column filters as History's other tabs. Rows come from the
 * `/history/prescription` endpoint, which reports how many services and lab
 * panels each prescription holds but not their names — so the last column
 * counts them, and the detail page names them. Every cell is short, so the
 * columns share the width equally.
 */
const PrescriptionTable: React.FC<PrescriptionTableProps> = ({
  rows,
  loading = false,
  showClient = false,
  banners,
  clearFiltersSlot,
  filterPersistKey,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const columns = useMemo<RecordColumn<HistoryPrescriptionListItem>[]>(
    () => [
      {
        key: "dateCreated",
        label: "Date created",
        flex: 1,
        filter: "date",
        date: (row) => row.dateCreated,
        value: (row) => formatDisplayDate(row.dateCreated),
      },
      ...(showClient
        ? [
            {
              key: "client",
              label: "Client",
              flex: 1,
              searchable: true,
              withInitials: true,
              optionKey: (row: HistoryPrescriptionListItem) => row.customerID,
              value: (row: HistoryPrescriptionListItem) =>
                historyClientName(row),
            } as RecordColumn<HistoryPrescriptionListItem>,
          ]
        : []),
      {
        key: "type",
        label: "Type",
        flex: 1,
        optionKey: (row) => String(row.sourceType),
        value: (row) => TYPE_LABELS[row.sourceType] ?? "—",
        render: (row) => <PrescriptionTypeBadge sourceType={row.sourceType} />,
      },
      {
        key: "items",
        label: "Items",
        flex: 1,
        filter: "none",
        value: itemsOf,
      },
      // Status last, as on the Assessments and Programming tabs.
      {
        key: "status",
        label: "Status",
        flex: 1,
        value: statusOf,
        render: (row) => (
          <PrescriptionStatusBadge
            isActive={row.active === 1}
            isExpired={Boolean(row.expired)}
          />
        ),
      },
    ],
    [showClient]
  );

  return (
    <RecordTable<HistoryPrescriptionListItem>
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={loading}
      banners={banners}
      skeletonRows={6}
      clearFiltersSlot={clearFiltersSlot}
      persistKey={filterPersistKey}
      emptyMessage="No prescriptions found"
      filteredEmptyMessage="No prescriptions match the selected filters"
      onRowClick={(row) =>
        navigate(`${location.pathname}/${row.id}${location.search}`)
      }
    />
  );
};

export default PrescriptionTable;
